import { useState } from 'react';
import { useStoresPaginated, useCreateStore, useUpdateStore, useDeleteStore, usePagination } from '@/hooks';
import type { Store } from '@geely/shared';
import type { StoreFormData } from '@geely/shared/schemas';
import { toast } from 'sonner';

export function useStoresData() {
  const { page, limit, setPage } = usePagination();
  const { data: paginatedResult, isLoading } = useStoresPaginated({ page, limit });
  const stores = paginatedResult?.data ?? [];
  const paginationMeta = paginatedResult?.meta;
  const createStore = useCreateStore();
  const updateStoreMutation = useUpdateStore();
  const deleteStoreMutation = useDeleteStore();

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingStore, setEditingStore] = useState<Store | null>(null);

  const handleOpenDialog = (store?: Store) => {
    setEditingStore(store || null);
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setEditingStore(null);
  };

  const handleSubmit = async (data: StoreFormData) => {
    try {
      if (editingStore) {
        await updateStoreMutation.mutateAsync({
          id: editingStore.id,
          data: {
            ...data,
            latitude: data.latitude ?? 0,
            longitude: data.longitude ?? 0,
          },
        });
        toast.success('Loja atualizada com sucesso!');
      } else {
        await createStore.mutateAsync({
          ...data,
          latitude: data.latitude ?? 0,
          longitude: data.longitude ?? 0,
        });
        toast.success('Loja criada com sucesso!');
      }
      handleCloseDialog();
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Erro ao salvar loja');
    }
  };

  const handleDelete = async (id: string) => {
    if (confirm('Tem certeza que deseja excluir esta loja?')) {
      try {
        await deleteStoreMutation.mutateAsync(id);
        toast.success('Loja excluída com sucesso!');
      } catch (err: any) {
        toast.error(err.response?.data?.error || 'Erro ao excluir loja');
      }
    }
  };

  return {
    stores,
    isLoading,
    paginationMeta,
    setPage,
    isDialogOpen,
    editingStore,
    handleOpenDialog,
    handleCloseDialog,
    handleSubmit,
    handleDelete,
  };
}
